import React, { useEffect } from 'react';
import { Col, message, Row, Typography } from 'antd';
import { CSVLink, UploadDragger } from '@common/components';
import {
  ImportMember,
  ValidateWorksMembershipImport,
  ValidateWorksMembershipImportResponse,
  ValidateWorksMembershipImportVariables,
} from 'works/legacy/graphql/validate-works-membership-import';
import { useMutation } from '@common/hooks';
import { RcFile } from 'antd/lib/upload';
import { CSVError } from 'works/legacy/lib/csv-error';

const { Text } = Typography;

/* eslint-disable prettier/prettier */
const organizationCsvTemplateContent = [
  ['Email', 'First Name', 'Last Name', 'Role', 'Employee ID', 'Phone Number'],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'organization_admin',
    '',
    '123-123-1233',
  ],
  ['name.lastname@domain.com', 'Name', 'Last Name', 'msp_admin', '321CBA', ''],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'program_admin',
    '123ABC',
    '',
  ],
  ['name.lastname@domain.com', 'Name', 'Last Name', 'program_manager', '', ''],
  ['name.lastname@domain.com', 'Name', 'Last Name', 'hiring_manager', '', ''],
  ['name.lastname@domain.com', 'Name', 'Last Name', 'billing_manager', '', ''],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'compliance_manager',
    '',
    '',
  ],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'staffing_coordinator',
    '',
    '',
  ],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'executive',
    '',
    '321-321-4321',
  ],
];

const supplierCsvTemplateContent = [
  ['Email', 'First Name', 'Last Name', 'Role', 'Employee ID', 'Phone Number'],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'supplier_admin',
    '321CBA',
    '123-123-1233',
  ],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'supplier_recruiter',
    '',
    '',
  ],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'supplier_admin',
    '123ABC',
    '',
  ],
  [
    'name.lastname@domain.com',
    'Name',
    'Last Name',
    'supplier_recruiter',
    '',
    '321-321-4321',
  ],
];
/* eslint-enable prettier/prettier */

type Props = {
  isOrganization: boolean;
  csvFile: RcFile | null;
  setCsvFile: (file: RcFile) => void;
  onSuccess: (members: ImportMember[]) => void;
  onErrors: (errors: CSVError[]) => void;
  onLoading: (isLoading: boolean) => void;
};

export function MembersAddMultipleUpload({
  isOrganization,
  csvFile,
  setCsvFile,
  onSuccess,
  onErrors,
  onLoading,
}: Props) {
  const [validateFile, { loading }] = useMutation<
    ValidateWorksMembershipImportResponse,
    ValidateWorksMembershipImportVariables
  >(ValidateWorksMembershipImport, {
    onCompleted({
      validateWorksMembershipImport: { success, errors, members },
    }) {
      if (success) {
        onSuccess(members);
      } else {
        onErrors(errors);
      }
    },
    onError: () =>
      message.error(
        'An error has occurred or your file is invalid. Try again later.'
      ),
  });

  useEffect(() => {
    onLoading(loading);
  }, [onLoading, loading]);

  const handleFileUpload = (file: RcFile) => {
    onSuccess([]);
    onErrors([]);
    setCsvFile(file);

    validateFile({
      variables: {
        input: {
          file,
        },
      },
    });

    return false;
  };

  return (
    <div className="members-add-multiple-upload-step">
      <Row>
        <Col span={24} style={{ marginBottom: 24 }}>
          <Text>
            <CSVLink
              data={
                isOrganization
                  ? organizationCsvTemplateContent
                  : supplierCsvTemplateContent
              }
              fileName="Works Users Template"
            >
              Download the .CSV template
            </CSVLink>{' '}
            to complete with users to add/update and upload it here:
          </Text>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <UploadDragger
            accept=".csv"
            fileList={csvFile ? [csvFile] : []}
            beforeUpload={handleFileUpload}
          />
        </Col>
      </Row>
      <style jsx>{`
        .members-add-multiple-upload-step :global(h1.ant-typography) {
          font-size: 30px;
        }
        .members-add-multiple-upload-step :global(span.ant-typography) {
          font-size: 16px;
        }
      `}</style>
    </div>
  );
}
